/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { ThemeProvider, css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Search_ProductHit as SearchProductHit } from '__generated__/graphql-types-do-not-use';
import type Maybe from 'graphql/tsutils/Maybe';
import { capitalize } from 'lodash';

import Imgix from 'js/components/Imgix';

import { Hidden, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import ProductTypePill from 'bundles/browse/components/Domain/ProductTypePill';
import ProductInfo from 'bundles/search-common/components/ProductInfo';
import { searchTheme } from 'bundles/search-common/searchTheme';
import { getSearchCardImageUrl } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/ent-website';

type Props = {
  hit: SearchProductHit;
};

type PropsForPartnerComponent = {
  partners?: Maybe<string[]>;
  partnerLogos?: Maybe<string[]>;
};

function SearchCardImage({ hit }: { hit: SearchProductHit }) {
  let altTagContent = hit.name ?? 'Product Logo';

  if (hit.partners?.length) {
    altTagContent = _t('#{productName} by #{partnerNameList}', {
      productName: hit.name,
      partnerNameList: hit.partners.join(', '),
    });
  }

  const ImageComponent = (
    <Imgix
      src={getSearchCardImageUrl({ objectID: hit?.id, partnerLogos: hit?.partnerLogos, imageUrl: hit?.imageUrl })}
      alt={altTagContent}
      imgParams={{ fit: 'fill', auto: 'format,compress', bg: 'FFF' }}
    />
  );

  return <div className="image-wrapper vertical-box">{ImageComponent}</div>;
}

function PartnerComponent({ partners, partnerLogos }: PropsForPartnerComponent) {
  const styles = {
    partner: css`
      font-size: 12px;
      color: var(--cds-color-grey-600);
      padding-left: var(--cds-spacing-100);
      line-height: 25px;
    `,
  };

  return (
    <div css={{ marginBottom: 'var(--cds-spacing-100)' }}>
      <Imgix
        src={partnerLogos?.[0] ?? ''}
        alt={partners?.[0] ?? ''}
        maxWidth={25}
        maxHeight={25}
        css={{ backgroundColor: 'var(--cds-color-white-0)', borderRadius: 3 }}
      />
      <Typography2 css={styles.partner} component="span">
        {partners?.[0] ?? ''}
      </Typography2>
    </div>
  );
}

function SearchResultCard({ hit }: Props) {
  const styles = {
    root: css`
      margin: var(--cds-spacing-300) 0;
    `,

    wrapper: css`
      display: flex;
      flex-direction: row;
      ${breakpoints.down('sm')} {
        flex-direction: row-reverse;
      }
    `,

    productInfo: css`
      flex: 1;
    `,

    productImage: css`
      width: 150px;
      height: 150px;
      margin-right: var(--cds-spacing-300);
      box-shadow: 0 0 9px -1px rgb(0 0 0/16%);

      img {
        width: 150px;
        height: 150px;
      }

      ${breakpoints.down('sm')} {
        width: 100px;
        height: 100px;
        margin-right: 0;

        img {
          width: 100px;
          height: 100px;
        }
      }
    `,
  };
  return (
    <ThemeProvider theme={searchTheme}>
      <div css={styles.root}>
        <div css={styles.wrapper}>
          <div css={styles.productImage}>
            <SearchCardImage hit={hit} />
          </div>
          <div css={styles.productInfo}>
            <Hidden mdUp>
              <PartnerComponent partners={hit?.partners} partnerLogos={hit.partnerLogos} />
            </Hidden>
            <Typography variant="h2" component="div" css={{ marginBottom: 'var(--cds-spacing-100)' }}>
              {hit.name}
            </Typography>
            <Hidden smDown>
              <Typography2 component="p" css={{ marginBottom: 'var(--cds-spacing-100)' }}>
                {hit.partners?.[0] ?? ''}
              </Typography2>
            </Hidden>
            <ProductTypePill productTypeName={hit?.productType} />
            <Hidden smDown>
              <ProductInfo
                tagline={
                  hit.productType === 'DEGREE' ||
                  (!hit.enrollments && !hit.avgProductRating && !hit.productDifficultyLevel)
                    ? hit.tagline
                    : undefined
                }
                enrollments={hit.enrollments}
                avgProductRating={hit.avgProductRating}
                numProductRatings={hit.numProductRatings}
                productDifficultyLevel={hit.productDifficultyLevel ? capitalize(hit.productDifficultyLevel) : undefined}
              />
            </Hidden>
          </div>
        </div>
        <Hidden mdUp css={{ marginTop: 'var(--cds-spacing-100)' }}>
          <ProductInfo
            tagline={
              hit.productType === 'DEGREE' || (!hit.enrollments && !hit.avgProductRating && !hit.productDifficultyLevel)
                ? hit.tagline
                : undefined
            }
            enrollments={hit.enrollments}
            avgProductRating={hit.avgProductRating}
            numProductRatings={hit.numProductRatings}
            productDifficultyLevel={hit.productDifficultyLevel ? capitalize(hit.productDifficultyLevel) : undefined}
          />
        </Hidden>
      </div>
    </ThemeProvider>
  );
}

export default SearchResultCard;
